const locale = process.env.NEXT_PUBLIC_LOCALE || "sv-SE";

const relativeTimeFormatter = new Intl.RelativeTimeFormat(locale, {
  numeric: "auto",
});

const longDateFormatter = new Intl.DateTimeFormat(locale, {
  timeZone: "Europe/Stockholm",
  day: "numeric",
  month: "long",
});

const shortDateFormatter = new Intl.DateTimeFormat(locale, {
  timeZone: "Europe/Stockholm",
  day: "numeric",
  month: "short",
});

const shortDateWithYearFormatter = new Intl.DateTimeFormat(locale, {
  timeZone: "Europe/Stockholm",
  day: "numeric",
  month: "short",
  year: "numeric",
});

const stockholmTimeFormatter = new Intl.DateTimeFormat(locale, {
  timeZone: "Europe/Stockholm",
  hour: "2-digit",
  minute: "2-digit",
  hour12: false,
});

const oneMinute = 60 * 1000;
const oneHour = 3600 * 1000;
const fiveHours = 5 * 3600 * 1000;
const oneDay = 24 * 3600 * 1000;

const formatSwedishEditorialTime = (date: Date) => {
  const parts = stockholmTimeFormatter.formatToParts(date);
  const hour = parts.find((part) => part.type === "hour")?.value;
  const minute = parts.find((part) => part.type === "minute")?.value;

  if (!hour || !minute) {
    throw new Error("Could not format time");
  }

  return `${hour}.${minute}`;
};

const FromDateTime = ({
  dateTime,
  showRelativeTime,
  bylineFormat,
}: {
  dateTime: string;
  showRelativeTime?: boolean;
  bylineFormat?: boolean;
}) => {
  const date = new Date(dateTime);
  const now = new Date(Date.now());
  const diff = now.getTime() - date.getTime();

  const getStockholmDateString = (date: Date) => {
    return date.toLocaleDateString(locale, {
      timeZone: "Europe/Stockholm",
      year: "numeric",
      month: "numeric",
      day: "numeric",
    });
  };

  const getStockholmDateParts = (date: Date) => {
    const dateString = getStockholmDateString(date);
    const [year, month, day] = dateString.split("-").map(Number);
    return { year, month, day };
  };

  const yearIfNotThisYear = (date: Date) => {
    const nowStockholm = getStockholmDateParts(now);
    const dateStockholm = getStockholmDateParts(date);

    if (dateStockholm.year !== nowStockholm.year) {
      return ` ${dateStockholm.year}`;
    }

    return "";
  };

  const dmt = (isoTimeString: string) => {
    const date = new Date(isoTimeString);
    const year = yearIfNotThisYear(date);
    const time = formatSwedishEditorialTime(date);

    return `${longDateFormatter.format(date)}${year}, ${time}`;
  };

  const shortDateFormat = (date: Date) => {
    const stockholmDate = getStockholmDateParts(date);
    const nowStockholm = getStockholmDateParts(now);
    const isSameYear = stockholmDate.year === nowStockholm.year;

    return isSameYear
      ? shortDateFormatter.format(date)
      : shortDateWithYearFormatter.format(date);
  };

  if (showRelativeTime) {
    if (!bylineFormat) {
      if (diff < oneMinute) {
        return relativeTimeFormatter.format(0, "second");
      }

      if (diff < oneHour) {
        const minutes = Math.floor(diff / oneMinute);
        return `${minutes} min`;
      }

      if (diff < fiveHours) {
        const hours = Math.floor(diff / oneHour);
        return `${hours} tim`;
      }
    }

    if (getStockholmDateString(now) === getStockholmDateString(date)) {
      const dayString = relativeTimeFormatter.format(0, "day");
      const capitalizedDay =
        dayString.charAt(0).toUpperCase() + dayString.slice(1);
      return `${capitalizedDay} ${formatSwedishEditorialTime(date)}`;
    }

    const yesterday = new Date(now.getTime() - oneDay);
    if (getStockholmDateString(yesterday) === getStockholmDateString(date)) {
      const dayString = relativeTimeFormatter.format(-1, "day");
      const capitalizedDay =
        dayString.charAt(0).toUpperCase() + dayString.slice(1);
      return `${capitalizedDay} ${formatSwedishEditorialTime(date)}`;
    }

    if (bylineFormat) {
      return shortDateFormat(date);
    }
  }

  return dmt(dateTime);
};

export { FromDateTime };
